import React, { createContext, useState, useEffect, useContext } from 'react';
import { fetchJson } from '../utils/fetchJson';
import { recordSessionEnd, SESSION_END_REASONS } from '../utils/sessionEnd.generated';

// The last profile the server confirmed. Not a security boundary — every request
// still carries the token and the server still decides what it allows — it only
// answers "who is signed in" while the network is down, which is the difference
// between a slow reload and being thrown out to the login form.
const LAST_USER_KEY = 'session.lastUser';

function normalizeRoleName(roleName) {
  if (!roleName) return roleName;
  const raw = String(roleName).trim();
  const key = raw.toLowerCase().replace(/\s+/g, '');
  const map = {
    superadmin: 'SystemAdmin',
    systemadmin: 'SystemAdmin',
    schooladministrator: 'SchoolAdmin',
    schooladmin: 'SchoolAdmin',
    headteacher: 'HeadMaster',
    headmaster: 'HeadMaster',
    principalheadteacher: 'Principal',
    principal: 'Principal',
    teacher: 'Teacher',
    classmaster: 'ClassMaster',
    subjecthead: 'SubjectHead',
    financeofficer: 'FinanceOfficer',
    admissionsofficer: 'AdmissionsOfficer',
    librarian: 'Librarian',
    transportofficer: 'TransportOfficer',
    inventorymanager: 'InventoryManager',
    nursehealthofficer: 'NurseHealthOfficer',
    supportagent: 'SupportAgent',
    salesrep: 'SalesRep',
    itmanager: 'ITManager',
    student: 'Student',
    parent: 'Parent',
  };
  return map[key] || raw;
}

export const UserContext = createContext(null);

// Custom hook to use UserContext
export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export function UserProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading, null = unauth

  useEffect(() => {
    let cancelled = false;

    const loadMe = async () => {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        if (!cancelled) setUser(null);
        return;
      }
      if (!cancelled) setUser(undefined);

      try {
        // fetchJson, NOT a bare fetch. This used the raw one, which meant a 401
        // here never even TRIED to refresh — it deleted the access token and put
        // the user on the login form, while a perfectly good refresh token sat
        // in storage doing nothing. The access token lasts eight hours, so this
        // signed people out roughly once a working day.
        const data = await fetchJson('/api/users/me');
        if (!cancelled && data) {
          const normalized = { ...data, role: normalizeRoleName(data.role) };
          setUser(normalized);
          if (normalized.schoolId) {
            localStorage.setItem('schoolId', normalized.schoolId);
          }
          // Kept so a reload with no network has something to open with.
          try { localStorage.setItem(LAST_USER_KEY, JSON.stringify(normalized)); } catch (_) {}
        }
      } catch (err) {
        // A 401 HERE IS A REAL SIGN-OUT, because fetchJson has already tried the
        // refresh token and been refused. Everything else — offline, a 500, a
        // timeout — must leave the session alone: `catch (_) { setUser(null) }`
        // signed people out for a dropped connection.
        if (err?.response?.status === 401) {
          // Write down WHY before clearing, so the login page can say something
          // truer than nothing. Being dropped here is otherwise indistinguishable
          // from having signed out.
          recordSessionEnd(SESSION_END_REASONS.TOKEN_REJECTED);
          try { localStorage.removeItem('accessToken'); } catch (_) {}
          try { localStorage.removeItem('token'); } catch (_) {}
          try { localStorage.removeItem(LAST_USER_KEY); } catch (_) {}
          if (!cancelled) setUser(null);
          return;
        }
        if (cancelled) return;
        let cached = null;
        try {
          const raw = localStorage.getItem(LAST_USER_KEY);
          cached = raw ? JSON.parse(raw) : null;
        } catch (_) { cached = null; }
        setUser((prev) => (prev !== undefined ? prev : cached));
      }
    };

    const authChangedHandler = () => {
      loadMe();
    };

    const storageHandler = (e) => {
      if (!e) return;
      if (e.key === 'accessToken' || e.key === 'token') {
        loadMe();
      }
    };

    loadMe();
    window.addEventListener('app:auth:changed', authChangedHandler);
    window.addEventListener('storage', storageHandler);
    return () => {
      cancelled = true;
      window.removeEventListener('app:auth:changed', authChangedHandler);
      window.removeEventListener('storage', storageHandler);
    };
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const avatarUrl = e && e.detail && e.detail.avatarUrl;
      if (!avatarUrl) return;
      setUser(prev => {
        if (!prev) return prev;
        return { ...prev, avatarUrl };
      });
    };
    window.addEventListener('app:user:avatar:updated', handler);
    return () => window.removeEventListener('app:user:avatar:updated', handler);
  }, []);

  return (
    <UserContext.Provider value={user}>
      {children}
    </UserContext.Provider>
  );
}
